import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, Sparkles, Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getRememberedEmail, forgetMemberEmail, MEMBER_EMAIL_EVENT } from "@/lib/memberEmail";
import { CallbackRequest } from "@/components/CallbackRequest";

/** Slim gold "Welcome back" bar for members remembered on this device (email match, no login). */
export function MemberWelcomeBar() {
  const [info, setInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const [callback, setCallback] = useState(false);
  const { pathname } = useLocation();

  const sendLink = async () => {
    setSending(true);
    try {
      const r = await api.post("/memberships/send-link", { email: getRememberedEmail() });
      if (r.data.sent) toast.success(`We've emailed your membership page to ${r.data.to}`);
      else toast.error("No active membership for that email — call 310-422-0669");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send the link — call 310-422-0669"); }
    finally { setSending(false); }
  };

  useEffect(() => {
    let alive = true;
    const check = () => {
      const email = getRememberedEmail();
      if (!email) { setInfo(null); return; }
      api.get("/memberships/check", { params: { email } })
        .then((r) => { if (!alive) return; if (r.data.member) setInfo(r.data); else { setInfo(null); forgetMemberEmail(); } })
        .catch(() => {});
    };
    check();
    window.addEventListener(MEMBER_EMAIL_EVENT, check);
    return () => { alive = false; window.removeEventListener(MEMBER_EMAIL_EVENT, check); };
  }, [pathname]);

  if (!info || pathname.startsWith("/admin")) return null;
  const perk = info.included_service && info.included_available
    ? "your included 30-minute session is ready"
    : `${info.discount_pct}% off every session and install`;

  return (
    <>
    <div className="border-b border-[#FFD600]/30 bg-[#FFD600]/10 text-sm" data-testid="member-welcome-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="flex items-center gap-2 text-[#FFD600] font-semibold" data-testid="member-welcome-text">
          <ShieldCheck className="w-4 h-4 shrink-0" /> Welcome back{info.first_name ? `, ${info.first_name}` : ""} — {info.tier_name} member
        </span>
        <span className="text-slate-200 flex items-center gap-1.5" data-testid="member-welcome-perk"><Sparkles className="w-3.5 h-3.5 text-[#FFD600] shrink-0" /> {perk}</span>
        <span className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-x-2 sm:gap-x-4 gap-y-1 py-1">
          <Link to="/ask" className="dg-link font-medium inline-flex items-center min-h-[40px] px-1" data-testid="member-welcome-ask">Priority ticket</Link>
          <Link to={info.included_available && info.included_service ? `/book?service=${info.included_service}` : "/book"} className="dg-link font-medium inline-flex items-center min-h-[40px] px-1" data-testid="member-welcome-book">
            {info.included_available && info.included_service ? "Book it — $0" : "Book — member price"}
          </Link>
          <button type="button" onClick={() => setCallback((v) => !v)} className="dg-link font-medium inline-flex items-center gap-1 min-h-[40px] px-1" data-testid="member-welcome-callback">
            <PhoneCall className="w-3 h-3" /> Call me back
          </button>
          <button type="button" onClick={sendLink} disabled={sending} className="dg-link font-medium inline-flex items-center gap-1 min-h-[40px] px-1 disabled:opacity-50" data-testid="member-welcome-my-membership">
            {sending && <Loader2 className="w-3 h-3 animate-spin" />} My membership
          </button>
          <button type="button" onClick={forgetMemberEmail} className="text-xs text-slate-400 hover:text-slate-200 transition-colors inline-flex items-center min-h-[40px] px-1 ml-auto sm:ml-0" data-testid="member-welcome-not-you">Not you?</button>
        </span>
      </div>
    </div>
    {callback && <CallbackRequest onClose={() => setCallback(false)} />}
    </>
  );
}
